'use client';

import { c as _c } from "react/compiler-runtime";
import { EVENT_DISPATCH_AMPLITUDE } from '@repo/analytics/analytics';
import { useAnalytics } from '@repo/analytics/analytics-provider';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { getHost } from '@repo/host/host';
import { useAppSelector } from '@repo/store/maps/hooks';
import { selectCurrentUser } from '@repo/store/maps/slices/auth';
import { selectFeatureFlag } from '@repo/store/maps/slices/featureFlags';
import { selectExternalMapId, selectPlayerLocationDeltas, selectTitleId, selectTitleName, selectTrainerConnected } from '@repo/store/maps/slices/mapData';
import type { MiniMapCtaExperimentParams, MiniMapCtaVariant } from '@repo/types/analytic-events/maps-analytic-events';
import { HostType } from '@repo/types/host';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addMiniMapCtaSeenKey, getMiniMapCtaSeenKey, getOrAssignMiniMapCtaVariant, hasSeenMiniMapCta, MINI_MAP_CTA_EXPERIMENT, MINI_MAP_CTA_SEEN_STORAGE_KEY, persistMiniMapCtaVariant } from './mini-map-cta-experiment';
import { useRemoteMiniMapLaunch } from './mini-map-launch-context';
const LAUNCH_SUCCESS_DURATION_MS = 6_000;
function initializeVariant(): MiniMapCtaVariant | null {
  if (typeof document === 'undefined') return null;
  const variant = getOrAssignMiniMapCtaVariant(document.cookie);
  persistMiniMapCtaVariant(variant);
  return variant;
}
export function useMiniMapCta(t0) {
  const $ = _c(49);
  const {
    isMiniMapMode,
    isMobileViewport
  } = t0;
  const analytics = useAnalytics();
  const platform = useHostType();
  const requestRemoteMiniMapOpen = useRemoteMiniMapLaunch();
  const mapId = useAppSelector(selectExternalMapId);
  const titleId = useAppSelector(selectTitleId);
  const titleName = useAppSelector(selectTitleName);
  const currentUser = useAppSelector(selectCurrentUser);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = selectFeatureFlag("maps_mini_map_cta");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const featureEnabled = useAppSelector(t1) !== undefined;
  const trainerConnected = useAppSelector(selectTrainerConnected);
  const playerLocationDeltas = useAppSelector(selectPlayerLocationDeltas);
  let t2;
  if ($[1] !== currentUser || $[2] !== titleId) {
    t2 = currentUser && titleId ? getMiniMapCtaSeenKey(currentUser.uuid, titleId) : null;
    $[1] = currentUser;
    $[2] = titleId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const seenKey = t2;
  const baseEligible = featureEnabled && !isMiniMapMode && !isMobileViewport && Boolean(playerLocationDeltas) && !!seenKey;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      key: null,
      seen: true
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const [seenState, setSeenState] = useState(t3);
  const isEligible = baseEligible && seenState.key === seenKey && !seenState.seen;
  const [variant, setVariant] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [successfulLaunchKey, setSuccessfulLaunchKey] = useState(null);
  const isLaunchSuccessful = successfulLaunchKey !== null && successfulLaunchKey === seenKey;
  const exposureTrackedRef = useRef(false);
  let t4;
  if ($[5] !== mapId || $[6] !== platform || $[7] !== titleId || $[8] !== titleName || $[9] !== variant) {
    t4 = variant ? {
      experiment: MINI_MAP_CTA_EXPERIMENT,
      variant,
      mapId: mapId ?? undefined,
      titleId: titleId ?? undefined,
      titleName: titleName ?? undefined,
      platform
    } : null;
    $[5] = mapId;
    $[6] = platform;
    $[7] = titleId;
    $[8] = titleName;
    $[9] = variant;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const analyticsParams = t4;
  let t5;
  let t6;
  if ($[11] !== seenKey) {
    t5 = () => {
      if (!seenKey) {
        setSeenState({
          key: null,
          seen: true
        });
        return;
      }
      const updateSeenState = () => {
        let seen;
        try {
          seen = hasSeenMiniMapCta(localStorage.getItem(MINI_MAP_CTA_SEEN_STORAGE_KEY), seenKey);
        } catch {
          seen = false;
        }
        setSeenState({
          key: seenKey,
          seen
        });
      };
      const handleStorage = event => {
        if (event.key === MINI_MAP_CTA_SEEN_STORAGE_KEY) {
          updateSeenState();
        }
      };
      updateSeenState();
      window.addEventListener("storage", handleStorage);
      return () => window.removeEventListener("storage", handleStorage);
    };
    t6 = [seenKey];
    $[11] = seenKey;
    $[12] = t5;
    $[13] = t6;
  } else {
    t5 = $[12];
    t6 = $[13];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[14] !== isEligible || $[15] !== isLaunchSuccessful) {
    t7 = () => {
      if (!isEligible) {
        if (!isLaunchSuccessful) {
          setIsOpen(false);
        }
        return;
      }
      setVariant(_temp);
      setIsOpen(true);
    };
    t8 = [isEligible, isLaunchSuccessful];
    $[14] = isEligible;
    $[15] = isLaunchSuccessful;
    $[16] = t7;
    $[17] = t8;
  } else {
    t7 = $[16];
    t8 = $[17];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[18] !== isLaunchSuccessful) {
    t9 = () => {
      if (!isLaunchSuccessful) {
        return;
      }
      const timeout = window.setTimeout(() => {
        setIsOpen(false);
        setSuccessfulLaunchKey(null);
      }, LAUNCH_SUCCESS_DURATION_MS);
      return () => window.clearTimeout(timeout);
    };
    t10 = [isLaunchSuccessful];
    $[18] = isLaunchSuccessful;
    $[19] = t10;
    $[20] = t9;
  } else {
    t10 = $[19];
    t9 = $[20];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[21] !== analytics || $[22] !== analyticsParams || $[23] !== isOpen) {
    t11 = () => {
      if (!isOpen) {
        exposureTrackedRef.current = false;
        return;
      }
      if (!analyticsParams || exposureTrackedRef.current) {
        return;
      }
      exposureTrackedRef.current = true;
      analytics.setUserProperty(MINI_MAP_CTA_EXPERIMENT, analyticsParams.variant, EVENT_DISPATCH_AMPLITUDE);
      analytics.event("game_map_mini_map_cta_impression", analyticsParams, EVENT_DISPATCH_AMPLITUDE);
    };
    t12 = [analytics, analyticsParams, isOpen];
    $[21] = analytics;
    $[22] = analyticsParams;
    $[23] = isOpen;
    $[24] = t11;
    $[25] = t12;
  } else {
    t11 = $[24];
    t12 = $[25];
  }
  useEffect(t11, t12);
  let t13;
  if ($[26] !== seenKey) {
    t13 = () => {
      if (!seenKey) {
        return;
      }
      try {
        const value = addMiniMapCtaSeenKey(localStorage.getItem(MINI_MAP_CTA_SEEN_STORAGE_KEY), seenKey);
        localStorage.setItem(MINI_MAP_CTA_SEEN_STORAGE_KEY, value);
      } catch {}
      setSeenState({
        key: seenKey,
        seen: true
      });
    };
    $[26] = seenKey;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const markSeen = t13;
  let t14;
  if ($[28] !== isLaunching || $[29] !== markSeen) {
    t14 = () => {
      if (isLaunching) {
        return;
      }
      markSeen();
      setSuccessfulLaunchKey(null);
      setIsOpen(false);
    };
    $[28] = isLaunching;
    $[29] = markSeen;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const close = t14;
  let t15;
  if ($[31] !== analytics || $[32] !== analyticsParams || $[33] !== isLaunching || $[34] !== markSeen || $[35] !== platform || $[36] !== requestRemoteMiniMapOpen || $[37] !== seenKey || $[38] !== trainerConnected) {
    t15 = async () => {
      if (!analyticsParams || !trainerConnected || isLaunching) {
        return;
      }
      setIsLaunching(true);
      analytics.event("game_map_mini_map_cta_launch_click", analyticsParams, EVENT_DISPATCH_AMPLITUDE);
      const success = await (platform === HostType.APP || platform === HostType.OVERLAY ? getHost().openMiniMap() : requestRemoteMiniMapOpen?.() ?? Promise.resolve(false)).catch(_temp2);
      analytics.event("game_map_mini_map_cta_launch_result", {
        ...analyticsParams,
        success
      }, EVENT_DISPATCH_AMPLITUDE);
      setIsLaunching(false);
      if (success) {
        setSuccessfulLaunchKey(seenKey);
        markSeen();
      }
    };
    $[31] = analytics;
    $[32] = analyticsParams;
    $[33] = isLaunching;
    $[34] = markSeen;
    $[35] = platform;
    $[36] = requestRemoteMiniMapOpen;
    $[37] = seenKey;
    $[38] = trainerConnected;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  const launch = t15;
  let t16;
  if ($[40] !== close || $[41] !== isLaunchSuccessful || $[42] !== isLaunching || $[43] !== isOpen || $[44] !== launch || $[45] !== titleName || $[46] !== trainerConnected || $[47] !== variant) {
    t16 = {
      close,
      isLaunchSuccessful,
      isLaunching,
      isOpen,
      launch,
      titleName,
      trainerConnected,
      variant
    };
    $[40] = close;
    $[41] = isLaunchSuccessful;
    $[42] = isLaunching;
    $[43] = isOpen;
    $[44] = launch;
    $[45] = titleName;
    $[46] = trainerConnected;
    $[47] = variant;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  return t16;
}
function _temp2() {
  return false;
}
function _temp(current) {
  return current ?? initializeVariant();
}